/**
 * WorkingDaysProgress Component
 *
 * Displays working days progress: "X of Y working days complete"
 * with a visual progress bar.
 *
 * Supports two modes:
 * - Default: Card with vertical stats layout
 * - Slim: Thin horizontal bar for compact display
 */

import React from 'react';

interface WorkingDaysProgressProps {
  total: number;
  elapsed: number;
  remaining: number;
  currentDayNumber: number;
  /** Slim mode: thin horizontal bar */
  slim?: boolean;
}

export const WorkingDaysProgress: React.FC<WorkingDaysProgressProps> = ({
  total,
  elapsed,
  remaining,
  currentDayNumber,
  slim = false,
}) => {
  const percentComplete = total > 0 ? Math.round((elapsed / total) * 100) : 0;

  // Slim mode: thin horizontal bar with inline stats (responsive)
  if (slim) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 py-2">
        {/* Mobile: stacked layout, Desktop: inline */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          {/* Label + Progress Bar row */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
              Working Days:
            </span>
            {/* Progress Bar - grows to fill space */}
            <div className="flex-1 bg-gray-200 rounded-full h-2.5 min-w-[60px]">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${percentComplete}%` }}
              />
            </div>
            {/* Percentage - always visible */}
            <span className="text-sm font-semibold text-blue-600 whitespace-nowrap">
              {percentComplete}%
            </span>
          </div>

          {/* Stats - wraps on mobile */}
          <div className="flex items-center gap-2 sm:gap-3 text-sm flex-wrap justify-center sm:justify-end">
            <span className="text-gray-700 whitespace-nowrap">
              <span className="font-semibold text-gray-900">{elapsed}</span>
              <span className="text-gray-500 text-xs sm:text-sm"> elapsed</span>
            </span>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <span className="text-gray-300 sm:hidden">·</span>
            <span className="text-gray-700 whitespace-nowrap">
              <span className="font-semibold text-blue-600">{remaining}</span>
              <span className="text-gray-500 text-xs sm:text-sm"> left</span>
            </span>
            <span className="text-gray-300 hidden sm:inline">|</span>
            <span className="text-gray-300 sm:hidden">·</span>
            <span className="text-gray-700 whitespace-nowrap">
              <span className="font-semibold text-gray-900">{total}</span>
              <span className="text-gray-500 text-xs sm:text-sm"> total</span>
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Default card mode
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Working Days Progress</h3>
        <span className="text-sm font-bold text-blue-600">{percentComplete}%</span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
        <div
          className="bg-blue-600 h-3 rounded-full transition-all duration-500"
          style={{ width: `${percentComplete}%` }}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-2xl font-bold text-gray-900">{elapsed}</div>
          <div className="text-xs text-gray-500">Days Elapsed</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-600">{remaining}</div>
          <div className="text-xs text-gray-500">Days Remaining</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-gray-900">{total}</div>
          <div className="text-xs text-gray-500">Total Days</div>
        </div>
      </div>

      {currentDayNumber > 0 && (
        <div className="mt-3 text-center text-xs text-gray-500">
          Today is working day #{currentDayNumber}
        </div>
      )}
    </div>
  );
};
